import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Activity, Radio, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 60000; // 60 s

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

function formatRel(iso) {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - ts);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function WatchlistDiffTicker() {
  const [events, setEvents] = useState([]);
  const [added24h, setAdded24h] = useState(0);
  const [removed24h, setRemoved24h] = useState(0);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const cursorRef = useRef(null);
  const intervalRef = useRef(null);

  const syncAll = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/watchlist/sync-all`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const jobCount = Object.keys(res.data?.jobs || {}).length;
      toast.success(`Kicked off ${jobCount} watchlist syncs — new adds/removes will appear here as they complete.`);
    } catch (err) {
      if (err?.response?.status === 409) {
        const detail = err.response.data?.detail || 'A sync is already running';
        toast.info(detail);
      } else {
        const detail = err?.response?.data?.detail || 'Failed to start syncs';
        toast.error(typeof detail === 'string' ? detail : 'Failed to start syncs');
      }
    } finally {
      setSyncing(false);
    }
  };

  const fetchDiff = async (mode) => {
    try {
      const params = { limit: mode === 'poll' ? 20 : 30 };
      if (mode === 'poll' && cursorRef.current) params.since = cursorRef.current;
      const res = await axios.get(`${API}/watchlist/diff/recent`, {
        params,
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setError(null);
      setAdded24h(res.data.added_24h || 0);
      setRemoved24h(res.data.removed_24h || 0);
      if (mode === 'poll' && cursorRef.current) {
        // Merge new events on top; dedupe by (source|entity_id|event|timestamp)
        const incoming = res.data.events || [];
        if (incoming.length) {
          setEvents(prev => {
            const seen = new Set(prev.map(e => `${e.source}|${e.entity_id}|${e.event}|${e.timestamp}`));
            const filtered = incoming.filter(e => !seen.has(`${e.source}|${e.entity_id}|${e.event}|${e.timestamp}`));
            return [...filtered, ...prev].slice(0, 30);
          });
        }
      } else {
        setEvents(res.data.events || []);
      }
      if (res.data.latest_timestamp) cursorRef.current = res.data.latest_timestamp;
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setError('access_denied');
      } else {
        setError('fetch_failed');
      }
    }
  };

  useEffect(() => {
    fetchDiff('initial');
  }, []);

  useEffect(() => {
    if (!live) return;
    intervalRef.current = setInterval(() => fetchDiff('poll'), POLL_MS);
    return () => clearInterval(intervalRef.current);
  }, [live]);

  if (error === 'access_denied') return null;

  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="watchlist-diff-ticker">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Radio className={`w-4 h-4 ${live ? 'text-emerald-500 animate-pulse' : 'text-slate-400'}`} />
          <h3 className="text-sm font-semibold text-slate-900">Watchlist changes (live)</h3>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-emerald-600" data-testid="ticker-added-24h">
            <TrendingUp className="w-3.5 h-3.5" /> +{added24h} added
          </span>
          <span className="flex items-center gap-1 text-rose-600" data-testid="ticker-removed-24h">
            <TrendingDown className="w-3.5 h-3.5" /> −{removed24h} removed
          </span>
          <button
            onClick={syncAll}
            disabled={syncing}
            className="px-2.5 py-1 border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
            data-testid="ticker-sync-all-btn"
            title="Kick off a fresh sync of every watchlist source"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Starting…' : 'Sync all'}
          </button>
          <button
            onClick={() => setLive(v => !v)}
            className="px-2.5 py-1 border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            data-testid="ticker-toggle-live"
          >
            {live ? 'Pause' : 'Resume'}
          </button>
        </div>
      </div>

      {error === 'fetch_failed' ? (
        <div className="text-center py-6 text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md" data-testid="ticker-error">
          Could not reach the watchlist diff feed. Retrying in {POLL_MS / 1000}s.
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-500 flex items-center justify-center gap-2" data-testid="ticker-empty">
          <Activity className="w-3.5 h-3.5" />
          No watchlist changes recorded yet. New adds/removes will appear here after the next sync.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
          {events.slice(0, 20).map(e => (
            <li
              key={`${e.source}-${e.entity_id}-${e.event}-${e.timestamp}`}
              className="py-2 flex items-center gap-3 text-sm"
              data-testid="ticker-event"
            >
              <Badge
                variant="outline"
                className={
                  e.event === 'added'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wide'
                    : 'bg-rose-50 text-rose-700 border-rose-200 text-[10px] uppercase tracking-wide'
                }
              >
                {e.event}
              </Badge>
              <span className="text-slate-900 truncate flex-1" title={e.name}>{e.name}</span>
              <span className="text-xs text-slate-500 shrink-0">{e.source}</span>
              <span className="text-xs text-slate-500 shrink-0 w-16 text-right">{formatRel(e.timestamp)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
